import { toastError } from 'admination-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useListActionsSink } from '@/core/components/list/listActions'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { SignaturePad } from '@/core/components/ui/signature-pad'
import { signNachweisOnGate } from '@/core/api/portal'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import type { NachweisItem } from '../../data/nachweise'

export function NachweiseTab({
  nachweise,
  onSigned,
}: {
  nachweise: NachweisItem[]
  onSigned: (dokumentId: string) => void
}) {
  const open = nachweise.filter((n) => !n.signed)
  const item = open.length > 0 ? open[open.length - 1] : null

  const [padOpen, setPadOpen] = useState(false)
  const signedRef = useRef<string | null>(null)

  const handleSubmit = useCallback(
    async (png: string) => {
      if (!item) return
      try {
        await signNachweisOnGate(item.id, png)
      } catch (e) {
        toastError(friendlyErrorMessage(e, 'Unterschrift konnte nicht gespeichert werden.'))
        throw e
      }
      signedRef.current = item.id
    },
    [item],
  )

  function handlePadClose() {
    setPadOpen(false)
    if (!signedRef.current) return
    const dokumentId = signedRef.current
    signedRef.current = null
    onSigned(dokumentId)
  }

  const sink = useListActionsSink()
  useEffect(() => {
    if (!sink) return
    if (!item) {
      sink(null)
      return
    }
    sink({
      actions: [{ id: 'sign', label: 'Unterschreiben', icon: 'i-signature', primary: true }],
      onRun: (id) => {
        if (id === 'sign') setPadOpen(true)
      },
    })
    return () => sink(null)
  }, [sink, item])

  return (
    <>
      <PdfRegion
        path={item?.bucketAddress ?? null}
        loadingLabel="Nachweis wird geladen …"
        placeholder={
          item ? 'Nachweis wird geladen…' : 'Zurzeit liegt kein Nachweis zur Unterschrift vor.'
        }
      />
      <SignaturePad
        open={padOpen}
        title={item ? `Nachweis · ${item.datum}` : 'Nachweis'}
        onSubmit={handleSubmit}
        onClose={handlePadClose}
      />
    </>
  )
}
